import {
  JSX,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import StyledContent from "../../styles/StyledContent";
import ChatListItem from "../ChatListItem/ChatListItem";
import LoadingDots from "../LoadingDots/LoadingDots";
import Prompt from "../Prompt/Prompt";
import AppContext from "../../contexts/AppContext";
import AppContextType from "../../types/AppContextType";
import ChatsContext from "../../contexts/ChatsContext";
import ConversationContext from "../../contexts/ConversationContext";
import DataEntryForm from "../DataEntryForm/DataEntryForm";
import {
  getConversationId,
  getConversationStarted,
  loadMessages,
  storeConversationStarted,
} from "../../api/messagingService";
import getLanguage from "../../helpers/language";
import useWebsockets from "../../hooks/useWebSockets";
import PromptSuggestionList from "../PromptSuggestionList/PromptSuggestionList";
import LoadingMessages from "../LoadingMessages/LoadingMessages";
import Chat from "../../types/Chat";

const Content = (): JSX.Element => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const {
    configuration,
    appId,
    publicKey,
    websocketConnection,
    setWebsocketConnection,
  } = useContext<AppContextType>(AppContext);
  const [chats, setChats] = useState<Chat[]>([]);
  const [lastChat, setLastChat] = useState<Chat | null>(null);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [isBotWriting, setIsBotWriting] = useState(false);
  const [isBotJustStartedWriting, setIsBotJustStartedWriting] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isConversationStarted, setIsConversationStarted] = useState(
    !!getConversationStarted(appId)
  );
  const [dataEntryFormSubmitted, setDataEntryFormSubmitted] = useState(false);
  const [conversationId, setConversationId] = useState<string>("");
  const [shouldRefreshConversation, setShouldRefreshConversation] =
    useState(false);

  const isNearChatListBottom = useCallback(() => {
    const scrollableContainer = contentRef.current;
    if (!scrollableContainer) return false;

    const gap =
      scrollableContainer.scrollTop -
      (scrollableContainer.scrollHeight -
        scrollableContainer.getBoundingClientRect().height);

    return Math.abs(gap) < 20;
  }, [chats]);

  const onScrollableContainerScroll = () => {
    const scrollableContainer = contentRef.current;
    if (!scrollableContainer) return;

    if (isNearChatListBottom()) {
      setIsUserScrolling(false);
      return;
    }

    setIsUserScrolling(true);
  };

  const scrollToBottom = () => {
    const scrollableContainer = contentRef.current;

    if (scrollableContainer && !isUserScrolling) {
      scrollableContainer.scrollTop =
        scrollableContainer.scrollHeight -
        scrollableContainer.getBoundingClientRect().height;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [chats]);

  const initializeMessages = async (): Promise<string | undefined> => {
    if (!configuration) return;
    if (!publicKey) return;
    if (!appId) return;

    setIsLoadingMessages(true);

    const localConversationId = await getConversationId(publicKey, appId);

    if (localConversationId) {
      if (isConversationStarted) {
        const messages = await loadMessages(publicKey, localConversationId);
        setIsLoadingMessages(false);
        setChats(messages);
      } else {
        setIsLoadingMessages(false);
      }
    } else {
      setIsLoadingMessages(false);
    }

    setConversationId(localConversationId);

    return localConversationId;
  };

  const refreshConversation = async () => {
    if (!configuration) return;
    if (!publicKey) return;
    if (!appId) return;

    // get the last message,
    setLastChat(chats[chats.length - 1]);

    // invalidate the session storage cache
    window.sessionStorage &&
      window.sessionStorage.removeItem(
        `dcinnovationChatBotConversation_${appId}`
      );

    // fetch the new conversation
    const localConversationId = await getConversationId(publicKey, appId);
    // setup the new conversation
    setConversationId(localConversationId);
  };

  useEffect(() => {
    const sendLastChat = () => {
      if (!lastChat) return;
      if (lastChat.role !== "user") return;
      if (!lastChat.content) return;

      // remove lastChat from the chat
      setChats((prevChats) => {
        return [...prevChats.slice(0, -1)];
      });

      // send the last message back to the new conversation
      sendChatMessage(lastChat.content);
      setLastChat(null);
    };

    sendLastChat();
  }, [conversationId]);

  useEffect(() => {
    const refresh = async () => {
      if (!shouldRefreshConversation) return;

      await refreshConversation();
      setShouldRefreshConversation(false);
    };

    refresh();
  }, [shouldRefreshConversation]);

  const sendChatMessage = async (
    message: string,
    initializeConversation: boolean = false
  ) => {
    if (!configuration) return;
    if (!publicKey) return;
    if (!appId) return;
    if (!websocketConnection) return;
    if (!conversationId) return;

    const newChatMessageObject = {
      role: "user",
      content: message,
      conversation_id: conversationId,
      language: getLanguage(),
    };

    websocketConnection
      .send(
        "send_message",
        appId,
        newChatMessageObject.conversation_id,
        newChatMessageObject.content,
        newChatMessageObject.language,
        initializeConversation
      )
      .catch((err) => console.error("Error sending message:", err));

    if (!initializeConversation) {
      // Make sure to do not visualize the initialization message
      setChats((prevChats) => {
        return [...prevChats, newChatMessageObject];
      });
    }

    setIsBotJustStartedWriting(true);
    setIsUserScrolling(false);
    setIsConversationStarted(true);
    storeConversationStarted(appId);
  };

  const initializeConversation = () => {
    sendChatMessage("Hello", true);
  };

  const shouldShowRating = () => {
    if (isLoadingMessages) return false;

    return !shouldShowDataEntryForm();
  };

  const shouldShowPrompt = () => {
    if (isLoadingMessages) return false;

    return !shouldShowDataEntryForm();
  };

  const hasUserDataPrompts = () => {
    if (!configuration) return false;
    if (!configuration.user_data_prompt) return false;
    if (!Array.isArray([configuration.user_data_prompt])) return false;

    return configuration.user_data_prompt.length > 0;
  };

  const shouldShowDataEntryForm = () => {
    if (isLoadingMessages) return false;
    if (chats.length > 0) return false;
    if (!configuration) return false;
    if (dataEntryFormSubmitted) return false;

    return hasUserDataPrompts();
  };

  const shouldShowPromptSuggestions = () => {
    if (shouldShowDataEntryForm()) return false;
    if (isLoadingMessages) return false;
    if (chats.length > 1) return false;
    if (isBotJustStartedWriting) return false;
    if (isBotWriting) return false;

    return configuration && configuration.prompt_suggestions.length > 0;
  };

  const shouldShowWaitingForBotResponseLoader = () => {
    return !isLoadingMessages && isBotJustStartedWriting && chats.length > 1;
  };

  const shouldBotTypingPlaceholder = () => {
    return !isLoadingMessages && isBotJustStartedWriting && chats.length === 0;
  };

  useWebsockets(
    setWebsocketConnection,
    setChats,
    setIsBotWriting,
    setIsBotJustStartedWriting,
    setShouldRefreshConversation
  );

  useEffect(() => {
    // When the form is been submitted initialize the conversation.
    if (!shouldShowDataEntryForm() && dataEntryFormSubmitted) {
      initializeConversation();
    }
  }, [dataEntryFormSubmitted]);

  useEffect(() => {
    if (!shouldShowDataEntryForm()) {
      initializeMessages();
    }
  }, []);

  useEffect(() => {
    // Initialize conversation when the data prompts are not present, the
    // data submitted is false and there are no chats yet.
    if (
      !hasUserDataPrompts() &&
      !dataEntryFormSubmitted &&
      conversationId &&
      chats.length === 0
    ) {
      initializeConversation();
    }
  }, [conversationId]);

  return (
    <ChatsContext.Provider
      value={{ chats: chats, sendChatMessage: sendChatMessage }}
    >
      <ConversationContext.Provider value={{ conversationId: conversationId }}>
        <StyledContent ref={contentRef} onScroll={onScrollableContainerScroll}>
          {shouldShowDataEntryForm() && configuration && (
            <DataEntryForm
              fields={configuration.user_data_prompt}
              setDataEntryFormSubmitted={setDataEntryFormSubmitted}
              initializeMessages={initializeMessages}
            />
          )}

          {!isLoadingMessages &&
            chats.length > 0 &&
            chats.map((chat: Chat, index: number) => {
              // Display the avatar with the processing state only in the last chat from the assistant.
              if (chats.length - 1 === index) {
                return (
                  <ChatListItem
                    key={index}
                    fromUser={chat.role === "user"}
                    isProcessing={isBotWriting}
                  >
                    {chat.content}
                  </ChatListItem>
                );
              } else {
                return (
                  <ChatListItem
                    key={index}
                    fromUser={chat.role === "user"}
                    isProcessing={false}
                  >
                    {chat.content}
                  </ChatListItem>
                );
              }
            })}

          {shouldShowWaitingForBotResponseLoader() && (
            <ChatListItem fromUser={false} isLoading>
              <LoadingDots />
            </ChatListItem>
          )}

          {shouldBotTypingPlaceholder() && (
            <ChatListItem fromUser={false} isLoading isProcessing>
              {""}
            </ChatListItem>
          )}

          {shouldShowPromptSuggestions() && configuration && (
            <PromptSuggestionList items={configuration.prompt_suggestions} />
          )}

          {isLoadingMessages && <LoadingMessages />}
        </StyledContent>
        {shouldShowPrompt() && (
          <Prompt disabled={isBotWriting} showRating={shouldShowRating()} />
        )}
      </ConversationContext.Provider>
    </ChatsContext.Provider>
  );
};

export default Content;
